import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Upload as UploadIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { userAtom } from '../../state/auth'
import { contactsAtom, contactsLoadingAtom, contactStatsAtom } from '../../state/contacts'
import { contactsApi, clientsApi, jobOpportunitiesApi, Contact, TargetCompanyTier } from '../../services/api'
import PortalAddJobDialog from '../../components/job-applications/PortalAddJobDialog'
import { useNotification } from '../../hooks/useNotification'
import PageHelpButton from '../../components/PageHelpButton'
import ImportLinkedInDialog from '../../components/networking/ImportLinkedInDialog'
import AddContactDialog from '../../components/networking/AddContactDialog'
import ContactFilters from '../../components/networking/ContactFilters'
import NetworkingContactsTable from '../../components/networking/NetworkingContactsTable'
import ContactDetailPanel from '../../components/networking/ContactDetailPanel'
import { buildTargetCompanyMap } from '../../utils/companyTier'
import { matchesStatusFilter } from '../../constants/networking'

export default function PortalNetworking() {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const [user] = useAtom(userAtom)
  const [contacts, setContacts] = useAtom(contactsAtom)
  const [loading, setLoading] = useAtom(contactsLoadingAtom)
  const [stats] = useAtom(contactStatsAtom)
  const [error, setError] = useState('')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { showSuccess } = useNotification()
  // MWA-297 — the contact who referred the job currently being added.
  const [addJobOpen, setAddJobOpen] = useState(false)
  const [referredByForJob, setReferredByForJob] = useState<{ id: string; name: string } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [referralInfo, setReferralInfo] = useState<{ contactId: string; name: string } | undefined>(undefined)
  const [hasZeroTargetMatches, setHasZeroTargetMatches] = useState(false)
  const [clientTargetRole, setClientTargetRole] = useState<string | undefined>()

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityOnly, setPriorityOnly] = useState(false)

  const clientId = user?.clientId || ''

  const fetchContacts = async () => {
    if (!clientId) return
    setLoading(true)
    try {
      const data = await contactsApi.list(clientId)
      setContacts(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to load contacts:', err)
      setError('Failed to load contacts. The networking service may not be available yet.')
    }
    setLoading(false)
  }

  const [targetCompanyMap, setTargetCompanyMap] = useState<Record<string, TargetCompanyTier>>({})

  useEffect(() => {
    fetchContacts()
    if (clientId) {
      clientsApi.get(clientId).then((client) => {
        setHasZeroTargetMatches(client.hasZeroTargetMatches === true)
        setClientTargetRole(client.dreamJob?.targetRole)
        setTargetCompanyMap(buildTargetCompanyMap(client.dreamJob?.targetCompanies))
      }).catch(() => {})
    }
  }, [clientId])

  // Deep-link: when the Portal Digest Follow-up button routes here with
  // ?contactId=<id>, auto-open that contact's detail panel once the
  // contact list has loaded. Strip the query param after opening so the
  // panel can be closed cleanly.
  useEffect(() => {
    const targetId = searchParams.get('contactId')
    if (!targetId || contacts.length === 0) return
    const match = contacts.find((c) => c.id === targetId)
    if (match) {
      setSelectedContact(match)
      setDetailOpen(true)
      navigate('/portal/networking', { replace: true })
    }
  }, [searchParams, contacts, navigate])

  // Apply filters
  const filteredContacts = useMemo(() => {
    let result = contacts
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          (c.company || '').toLowerCase().includes(q) ||
          (c.position || '').toLowerCase().includes(q)
      )
    }
    if (statusFilter) result = result.filter((c) => matchesStatusFilter(c.status, statusFilter))
    if (priorityOnly) result = result.filter((c) => c.isPriority)
    return result
  }, [contacts, search, statusFilter, priorityOnly])

  const hasActiveFilters = !!search || !!statusFilter || priorityOnly

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact)
    setDetailOpen(true)
  }

  const handleContactUpdate = (updated: Contact) => {
    setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
    setSelectedContact(updated)
  }

  const handleContactDelete = (id: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== id))
  }

  const handleContactCreated = (contact: Contact) => {
    setContacts((prev) => [...prev, contact])
  }

  const handleAddReferral = (referredByContactId: string, referredByName: string) => {
    setReferralInfo({ contactId: referredByContactId, name: referredByName })
    setAddOpen(true)
  }

  // MWA-297 — the client recording a job somebody in their network told them
  // about. Captured on the contact, which is where they are standing when they
  // hear it, rather than sending them to Job Search to start over.
  const handleAddReferredOpportunity = (contactId: string, contactName: string) => {
    setReferredByForJob({ id: contactId, name: contactName })
    setAddJobOpen(true)
  }

  // The link rides along on the create, so the opportunity never exists without
  // its referral recorded. awaitingDecision keeps it out of "Applied" — the
  // client has been told about this job, not applied to it.
  const handleAddReferredJob = async (data: {
    company: string
    jobTitle: string
    applicationDate?: string
    url?: string
    notes?: string
    location?: string
  }): Promise<void> => {
    await jobOpportunitiesApi.create({
      ...data,
      clientId,
      awaitingDecision: true,
      linkedContactIds: referredByForJob ? [referredByForJob.id] : [],
    })
    showSuccess(
      referredByForJob
        ? `Added, with ${referredByForJob.name} recorded as the referral`
        : 'Job added'
    )
    setAddJobOpen(false)
    setReferredByForJob(null)
  }

  // targetCompanyMap is now populated from client data in the useEffect above

  // A skeleton in the list's own shape rather than a centred spinner: the
  // spinner threw the whole page away and then rebuilt it, so every load
  // flashed. This holds the layout the contacts will land in.
  if (loading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1">
            Side Door
          </Typography>
          <PageHelpButton articleId="page-portal-networking" />
        </Stack>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Contacts are organized by target company, connection type, and priority.
        </Typography>
        <NetworkingContactsTable
          contacts={[]}
          targetCompanyMap={{}}
          onContactClick={() => {}}
          audience="client"
          loading
        />
      </Box>
    )
  }

  // Empty state
  if (contacts.length === 0 && !loading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1">
            Side Door
          </Typography>
          <PageHelpButton articleId="page-portal-networking" />
        </Stack>

        <Paper sx={{ p: 6, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <GroupsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Import your LinkedIn network
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Upload your LinkedIn connections to start tracking your outreach.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
            sx={{ mb: 2 }}
          >
            Import from LinkedIn
          </Button>
          <Accordion disableGutters elevation={0} sx={{ textAlign: 'left', bgcolor: 'transparent' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}>
              <Typography variant="body2" color="primary">
                How do I export my LinkedIn connections?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Go to LinkedIn and click on your profile icon</li>
                <li>Select "Settings & Privacy"</li>
                <li>Click "Data privacy" in the left sidebar</li>
                <li>Click "Get a copy of your data"</li>
                <li>Select "Connections" and click "Request archive"</li>
                <li>Wait for LinkedIn to email you the download link</li>
                <li>Download and unzip the file — look for "Connections.csv"</li>
              </ol>
            </AccordionDetails>
          </Accordion>
        </Paper>

        <ImportLinkedInDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          clientId={clientId}
          onImportComplete={fetchContacts}
        />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">
          Side Door
        </Typography>
        <PageHelpButton articleId="page-portal-networking" />
      </Stack>
      {/* MWA-289 — describes the list below: grouped by Dream/Target/Back-Up/
          Wildcard/Unlisted company, each contact carrying a connection type and a priority flag.
          Only reached once contacts exist; the import screen returns earlier. */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Contacts are organized by target company, connection type, and priority.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {hasZeroTargetMatches && contacts.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          We noticed you don't have any connections at your target companies yet. Your coach has been notified and will follow up with next steps.
        </Alert>
      )}

      {/* Actions. The filters themselves now live in the table's own filter
          panel, so every control that narrows the list sits in one place —
          and on a phone, inside one sheet. */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setImportOpen(true)}
          sx={{ minHeight: 44, flex: { xs: '1 1 0', sm: '0 0 auto' } }}
        >
          Import More
        </Button>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{ minHeight: 44, flex: { xs: '1 1 0', sm: '0 0 auto' } }}
        >
          Add Contact
        </Button>
      </Box>

      <NetworkingContactsTable
        contacts={filteredContacts}
        targetCompanyMap={targetCompanyMap}
        onContactClick={handleContactClick}
        audience="client"
        hasActiveFilters={hasActiveFilters}
        totalCount={stats.total}
        priorityCount={stats.priority}
        onClearFilters={() => { setSearch(''); setStatusFilter(''); setPriorityOnly(false) }}
        filterSlot={
          <ContactFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            priorityOnly={priorityOnly}
            onPriorityOnlyChange={setPriorityOnly}
            stacked={isPhone}
          />
        }
      />

      {/* Dialogs */}
      <ImportLinkedInDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        clientId={clientId}
        onImportComplete={fetchContacts}
      />
      <AddContactDialog
        open={addOpen}
        onClose={() => { setAddOpen(false); setReferralInfo(undefined) }}
        clientId={clientId}
        onCreated={handleContactCreated}
        referredBy={referralInfo}
      />
      <ContactDetailPanel
        contact={selectedContact}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={handleContactUpdate}
        onDelete={handleContactDelete}
        onAddReferral={handleAddReferral}
        onAddReferredOpportunity={handleAddReferredOpportunity}
        clientTargetRole={clientTargetRole}
        audience="client"
      />
      <PortalAddJobDialog
        open={addJobOpen}
        onClose={() => {
          setAddJobOpen(false)
          setReferredByForJob(null)
        }}
        onAdd={handleAddReferredJob}
        referredByName={referredByForJob?.name}
      />
    </Box>
  )
}
